import ItemIcon from 'app/frontend/base/components/ItemIcon'
import { EventActivitiesPlanningInfo } from 'app/frontend/base/api/event'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { AlertTriangle, Clock } from 'lucide-react'
import { PropsWithChildren, ReactNode } from 'react'
import WishlistAlternativeTooltipContent, {
  WishlistAlternative,
} from 'app/frontend/base/components/WishlistAlternativeTooltipContent'
import WishLegacyPriorityDot from 'app/frontend/base/components/WishLegacyPriorityDot'
import WishLegacyPriority from 'app/frontend/types/custom/WishLegacyPriority'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { getSpecialization } from 'app/frontend/base/services/characterValues'

const baseWowheadUrl = `https://wowhead.com/`

type EncounterWishlistItem =
  EventActivitiesPlanningInfo['activities'][string]['wishlist_items'][number][number]

type Props = PropsWithChildren<{
  items: EncounterWishlistItem[]
}>

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const manualUpgradeRank: Record<WishLegacyPriority, number> = {
  huge: 4,
  big: 3,
  small: 2,
  tiny: 1,
  none: 0,
}

const formatSpecUpgrades = (item: EncounterWishlistItem): ReactNode => {
  const entries = (item.specs ?? [])
    .map((spec) => ({
      specialization: getSpecialization(spec.id.toString()),
      manual:
        typeof spec.percentage === 'string' &&
        spec.percentage in manualUpgradeRank
          ? (spec.percentage as WishLegacyPriority)
          : null,
      percentage: toNumber(spec.percentage),
    }))
    .filter((entry) => entry.manual || (entry.percentage ?? 0) > 0)
    .sort((a, b) =>
      a.manual || b.manual
        ? (b.manual ? manualUpgradeRank[b.manual] : 0) -
          (a.manual ? manualUpgradeRank[a.manual] : 0)
        : (b.percentage ?? 0) - (a.percentage ?? 0),
    )

  if (entries.length === 0) return null

  return (
    <span className='flex items-center gap-2'>
      {entries.map((entry, index) => (
        <span key={index} className='flex items-center gap-1'>
          {entry.specialization ? (
            <BlizzardIcon
              iconName={entry.specialization.icon}
              title={entry.specialization.name}
              size='tiny'
              className='rounded-sm'
            />
          ) : null}
          {entry.manual ? (
            <WishLegacyPriorityDot priority={entry.manual} />
          ) : (
            `${entry.percentage?.toLocaleString('en-US', {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })}%`
          )}
        </span>
      ))}
    </span>
  )
}

const formatUpgrade = (item: EncounterWishlistItem): ReactNode => {
  const specUpgrades = formatSpecUpgrades(item)
  if (specUpgrades) return specUpgrades

  const manualUpgrades = item.score?.manual ?? []
  if (manualUpgrades.length > 0) {
    return (
      <span className='flex items-center gap-1'>
        {manualUpgrades.map((upgrade, index) => (
          <WishLegacyPriorityDot
            key={`${upgrade}:${index}`}
            priority={upgrade}
          />
        ))}
      </span>
    )
  }

  const percentage = toNumber(item.score?.percentage)
  if (percentage !== null) {
    return `${percentage.toLocaleString('en-US', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    })}%`
  }

  return '-'
}

const EncounterWishlistTooltip = ({ children, items }: Props) => {
  return (
    <HoverCard openDelay={400} closeDelay={100}>
      <HoverCardTrigger className='cursor-default'>{children}</HoverCardTrigger>
      <HoverCardContent className='text-xs w-fit'>
        <table className='whitespace-nowrap [&_td]:px-1 [&_th]:px-1 text-left'>
          <thead>
            <tr>
              <th>Item</th>
              <th>Upgrade</th>
              <th className='flex items-end'>
                <Tooltip>
                  <TooltipTrigger>
                    <Clock size={14} />
                  </TooltipTrigger>
                  <TooltipContent>Age in days</TooltipContent>
                </Tooltip>
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const alternative = (item.alternative ??
                null) as WishlistAlternative | null

              if (item.score?.percentage === null) {
                return null
              }

              return (
                <tr key={`${item.id}:${index}`} className='[&_td]:py-0.5'>
                  <td>
                    <a
                      target='_blank'
                      rel='noreferrer'
                      href={`${baseWowheadUrl}item=${item.id}`}
                    >
                      <ItemIcon id={item.id} className='w-4 h-4' />
                    </a>
                  </td>
                  <td>{formatUpgrade(item)}</td>
                  <td
                    className={`flex justify-center ${
                      item.age && item.age > 21
                        ? 'text-destructive-foreground'
                        : item.age && item.age > 7
                        ? 'text-warning-foreground'
                        : 'text-success-foreground'
                    }`}
                  >
                    {item.age}
                  </td>
                  <td>
                    {alternative ? (
                      <Tooltip>
                        <TooltipTrigger>
                          <AlertTriangle
                            size={12}
                            className='text-warning-foreground shrink-0'
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <WishlistAlternativeTooltipContent
                            alternative={alternative}
                            valueDisplay='percentage'
                          />
                        </TooltipContent>
                      </Tooltip>
                    ) : null}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </HoverCardContent>
    </HoverCard>
  )
}

export default EncounterWishlistTooltip
